import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { TabList, TabPanel, TabContext } from '@mui/lab';
import {
  Box,
  Tab,
  Grid,
  Stack,
  Alert,
  Button,
  Container,
  Typography,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';
import { titleHeadingStyle, titleHeadingStyleSmole } from 'src/pages/style';

import ActionButton from 'src/components/action-button/action-button';

import StatusGrid from 'src/sections/statuses/grid/statusGrid';

import AddStatus from './modals/AddStatus';
import EditStatus from './modals/EditStatus';
import { useAuth } from '../../hooks/useAuth';
import StatusTable from './table/statusTable';
import Iconify from '../../components/iconify';
import { useModal } from '../../hooks/useModal';
import FilterPopover from './popovers/filterPopover';
import DeleteStatusDeptModal from './modals/DeleteStatusModal';
import { statusColorMapper } from '../../utils/statusColorMapper';
import UserListToolbar from '../../components/ToolBars/FormToolbar';
import { fetchDepartments } from '../../services/department.service';
import { getComparator, applySortFilter } from '../../utils/filterData';
import { getUsersByDepartment, getUsersByDepartments } from '../../services/user.service';
import {
  modifyStatus,
  createStatus,
  deleteStatus,
  fetchStatuses,
} from '../../services/status.service';

const itemsPerPage = 10;

export default function StatusView() {
  const { t } = useTranslation();
  const { showToast, hideToast } = useToast();
  const { showModal, hideModal } = useModal();
  const { isSmallScreen } = useResponsiveScreen();

  const { permissions, departmentIds: rawDepartmentIds } = useAuth();

  const departmentIds = useMemo(
    () => rawDepartmentIds || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(rawDepartmentIds)]
  );

  const canCreate = permissions?.includes('TICKET_STATUS:CREATE');
  const canReadGlobal = permissions?.includes('TICKET_STATUS:READ');
  const canUpdate = permissions?.includes('TICKET_STATUS:UPDATE');
  const canDelete = permissions?.includes('TICKET_STATUS:DELETE');
  const canReadDepartmentsGlobal = permissions?.includes('DEPARTMENT:READ');

  const [departmentList, setDepartmentList] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(0);
  const [filteredStatusData, setFilteredStatusData] = useState([]);
  const [emailsList, setEmailsList] = useState([]);
  const [filterName, setFilterName] = useState('');
  const [tabValue, setTabValue] = useState('1');
  const [gridPage, setGridPage] = useState(0);
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('statusId');
  const [isLoading, setIsLoading] = useState(false);
  const [filterPopover, setFilterPopover] = useState(null);
  const [searchBy, setSearchBy] = useState('byName');
  const [filterBy, setFilterBy] = useState('name');

  const [subFilterPopover, setSubFilterPopover] = useState({
    department: null,
    systemState: null,
  });

  const getDepartMents = useCallback(async () => {
    genericApiToaster(
      fetchDepartments,
      [],
      '',
      t('errorMessages.couldntLoadDep'),
      showToast,
      hideToast,
      t,
      (res) => {
        const filteredDeps = canReadDepartmentsGlobal
          ? res
          : res.filter((dep) => (departmentIds || []).includes(dep.id));
        setDepartmentList(filteredDeps);
      }
    );
  }, [t, showToast, hideToast, canReadDepartmentsGlobal, departmentIds]);

  const getStatusSuccesCallback = useCallback(
    (res) => {
      const baseData = res.filter((status) => status.name !== 'Hidden');

      const groupedMap = baseData.reduce((acc, status) => {
        const key = `${status.name}-${status.color}-${status.systemMappedStatus}`;
        if (!acc[key]) {
          acc[key] = {
            ...status,
            statusColor: status.statusId < 6 ? statusColorMapper(status) : status.color,
            allDbIds: [status.statusId],
            allDepartmentIds: [status.departmentId],
            allDepartmentNames:
              status.departmentId === 0
                ? [t('pages.statusPage.globalStatus')]
                : [status.department],
          };
        } else {
          acc[key].allDbIds.push(status.statusId);
          acc[key].allDepartmentIds.push(status.departmentId);
          acc[key].allDepartmentNames.push(
            status.departmentId === 0
              ? t('pages.statusPage.globalStatus')
              : status.department
          );
        }
        return acc;
      }, {});

      const aggregatedList = Object.values(groupedMap);

      const filtered = aggregatedList.filter((status) => {
        const isGlobal = status.allDepartmentIds.includes(0) || status.statusId < 6;
        const isAuthorized = status.allDepartmentIds.some((id) =>
          (departmentIds || []).includes(id)
        );

        if (selectedDepartmentId === 0) {
          return canReadGlobal || isGlobal || isAuthorized;
        }
        return status.allDepartmentIds.includes(selectedDepartmentId);
      });

      setFilteredStatusData(filtered);
      setTablePage(0);
      setGridPage(0);

      if (selectedDepartmentId === 0) {
        if (!canReadGlobal && departmentIds?.length > 0) {
          getUsersByDepartments(departmentIds).then((resUsers) => {
            setEmailsList(resUsers.filter((u) => !u.archived).map((u) => u.email));
          });
        }
      } else {
        getUsersByDepartment(selectedDepartmentId).then((resUsers) => {
          setEmailsList(resUsers.filter((u) => !u.archived).map((u) => u.email));
        });
      }
    },
    [t, canReadGlobal, departmentIds, selectedDepartmentId]
  );

  const getStatuses = useCallback(() => {
    setIsLoading(true);
    genericApiToaster(
      fetchStatuses,
      [departmentIds],
      '',
      t('errorMessages.couldntLoadStatuses'),
      showToast,
      hideToast,
      t,
      getStatusSuccesCallback,
      undefined,
      () => setIsLoading(false)
    );
    // Added departmentIds as a dependency
  }, [t, showToast, hideToast, getStatusSuccesCallback, departmentIds]);

  useEffect(() => {
    getDepartMents();
  }, [getDepartMents]);
  useEffect(() => {
    getStatuses();
  }, [getStatuses]);

  const handleAddStatus = (newStatusData) => {
    genericApiToaster(
      createStatus,
      newStatusData,
      t('successMessages.successCreatingStatus'),
      t('errorMessages.couldntCreateStatus'),
      showToast,
      hideToast,
      t,
      getStatuses
    );
  };

  const handleEditStatus = (editedStatusData) => {
    genericApiToaster(
      modifyStatus,
      editedStatusData,
      t('successMessages.successModifyingStatus'),
      t('errorMessages.couldntModifyStatus'),
      showToast,
      hideToast,
      t,
      getStatuses
    );
  };

  const handleDeleteTrigger = (status) => {
    if (status.allDbIds.length <= 1 || status.statusId <= 5) {
      showModal({
        title: t('pages.ticketDetails.confirmDelete'),
        Component: () => (
          <>
            <Alert
              severity="warning"
              sx={{ fontSize: isSmallScreen ? '0.775rem' : '1rem', mb: 2 }}
            >
              {t('pages.ticketDetails.areYouSure')} {status.name}?
            </Alert>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                id="modal-confirm-btn"
                sx={{ color: 'red', fontSize: isSmallScreen ? '0.775rem' : '1rem' }}
                onClick={() => {
                  genericApiToaster(
                    deleteStatus,
                    status.statusId,
                    t('successMessages.successDeletingStatus'),
                    t('errorMessages.couldntDeleteStatus'),
                    showToast,
                    hideToast,
                    t,
                    getStatuses
                  );
                  hideModal();
                }}
              >
                {t('buttons.confirm')}
              </Button>
              <Button
                id="modal-cancel-btn"
                type="button"
                onClick={hideModal}
                sx={{ fontSize: isSmallScreen ? '0.775rem' : '1rem' }}
              >
                {t('buttons.cancel')}
              </Button>
            </Stack>
          </>
        ),
        props: { onClose: hideModal },
      });
    } else {
      showModal({
        title: t('pages.statusPage.deleteFromDepartments'),
        Component: DeleteStatusDeptModal,
        props: {
          status,
          hideModal,
          onDelete: (idsToDelete) => {
            const promises = idsToDelete.map((id) => deleteStatus(id));
            Promise.all(promises).then(() => {
              showToast({
                message: t('successMessages.successDeletingStatus'),
                severity: 'success',
              });
              getStatuses();
              hideModal();
            });
          },
        },
      });
    }
  };

  const handleDetailsClick = (status) => {
    showModal({
      title: t('pages.statusPage.editStatusForm'),
      Component: EditStatus,
      props: {
        hideModal,
        status,
        canUpdate,
        departmentList: departmentList.filter((dep) =>
          (departmentIds || []).includes(dep.id)
        ),
        emailsList,
        editStatus: handleEditStatus,
      },
    });
  };

  const filteredStatus = useMemo(() => {
    const mappedData = filteredStatusData.map((status) => ({
      ...status,
      name: status.statusId < 6 ? t(`status.${status.name}`) : status.name,
      displayDepartments: [...new Set(status.allDepartmentNames)].join(', '),
    }));
    return applySortFilter(
      mappedData,
      getComparator(order, orderBy),
      filterName,
      filterBy
    );
  }, [filteredStatusData, order, orderBy, filterName, filterBy, t]);

  const { isNotFound, searchIsNotFound } = useMemo(() => {
    const isNotFound = !filteredStatus.length;
    const searchIsNotFound = !filteredStatus.length && !!filterName;
    return { isNotFound, searchIsNotFound };
  }, [filteredStatus, filterName]);

  const handleTabChange = (event, newValue) => setTabValue(newValue);
  const handleFilterByName = (event) => {
    setGridPage(0);
    setTablePage(0);
    setFilterName(event.target.value);
  };
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <>
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={isSmallScreen ? 2 : 5}
        >
          <Typography
            style={isSmallScreen ? titleHeadingStyleSmole : titleHeadingStyle}
            mx={1}
          >
            {t('pages.statusPage.statusPlural')}
          </Typography>
          <Grid>
            {canCreate && (
              <ActionButton
                id="add-status-btn"
                onClick={() =>
                  showModal({
                    title: t('pages.statusPage.addStatusForm'),
                    Component: AddStatus,
                    props: {
                      hideModal,
                      emailsList,
                      departmentList: departmentList.filter((dep) =>
                        (departmentIds || []).includes(dep.id)
                      ),
                      canUpdate,
                      addStatus: handleAddStatus,
                      currentUserDepartment:
                        selectedDepartmentId !== 0
                          ? Number(selectedDepartmentId)
                          : departmentIds?.[0]
                          ? Number(departmentIds[0])
                          : 0,
                    },
                  })
                }
                icon={isSmallScreen ? 'octicon:plus-circle-16' : 'eva:plus-fill'}
                text={t('pages.statusPage.newStatus')}
                title={t('pages.statusPage.newStatus')}
              />
            )}
          </Grid>
        </Stack>

        <Box mb={isSmallScreen ? 0 : 1} mt={isSmallScreen ? 0 : 1}>
          <UserListToolbar
            numSelected={0}
            model={t('pages.statusPage.statusSingle')}
            onFilterName={handleFilterByName}
            filterName={filterName}
            setFilterName={setFilterName}
            filterBy={searchBy}
            handleOpenFilter={(e) => setFilterPopover(e.currentTarget)}
            handleCloseFilter={() => setFilterPopover(null)}
          />
        </Box>

        <TabContext value={tabValue}>
          <Stack direction="row" sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
            <TabList onChange={handleTabChange}>
              <Tab
                onClick={() => setOrderBy('statusId')}
                icon={<Iconify icon="gridicons:grid" />}
                value="1"
              />
              <Tab icon={<Iconify icon="gridicons:list-unordered" />} value="2" />
            </TabList>
          </Stack>
          <TabPanel value="1" sx={{ marginY: 3, padding: 0 }}>
            <StatusGrid
              statusData={filteredStatus}
              itemsPerPage={itemsPerPage}
              page={gridPage}
              handleChangePage={(e, p) => setGridPage(p - 1)}
              handleDetailsClick={handleDetailsClick}
              deleteStatus={handleDeleteTrigger}
              isNotFound={isNotFound}
              searchIsNotFound={searchIsNotFound}
              filterName={filterName}
              isLoading={isLoading}
              canUpdate={canUpdate}
              canDelete={canDelete}
            />
          </TabPanel>
          <TabPanel value="2" sx={{ marginY: 3, padding: 0 }}>
            <StatusTable
              statusData={filteredStatus}
              page={tablePage}
              rowsPerPage={rowsPerPage}
              handleChangePage={(e, p) => setTablePage(p)}
              handleChangeRowsPerPage={(e) => {
                setTablePage(0);
                setRowsPerPage(parseInt(e.target.value, 10));
              }}
              handleRequestSort={handleRequestSort}
              order={order}
              orderBy={orderBy}
              handleDetailsClick={handleDetailsClick}
              deleteStatus={handleDeleteTrigger}
              isNotFound={isNotFound}
              searchIsNotFound={searchIsNotFound}
              filterName={filterName}
              isLoading={isLoading}
              canUpdate={canUpdate}
              canDelete={canDelete}
            />
          </TabPanel>
        </TabContext>
      </Container>
      <FilterPopover
        setSubFilterPopover={setSubFilterPopover}
        subFilterPopover={subFilterPopover}
        handleCloseSubFilter={() =>
          setSubFilterPopover({ department: null, systemState: null })
        }
        setSearchBy={setSearchBy}
        handleCloseFilter={() => setFilterPopover(null)}
        filterPopover={filterPopover}
        setFilterTitle={setFilterName}
        setFilterBy={setFilterBy}
        departmentData={departmentList}
        setPage={setTablePage}
        setSelectedDepartmentId={setSelectedDepartmentId}
      />
    </>
  );
}
